'use client';

import type { ProfileModuleDto } from '@up2stay/up2promo-api-client';
import { Download, File } from 'lucide-react';

import { Button } from '../../components/button';
import { cn } from '../../utils/cn';
import { useProfileLayout } from '../layouts/profile-layout-context';
import {
  ModuleEntryCard,
  ModuleEntryIcon,
  ModuleEntryRow,
  ModuleEntryTitleBlock,
} from './module-entry-card';
import {
  resolvePublicProfileModuleChrome,
  type PublicProfileModuleChrome,
} from '../public-profile-module-copy';

interface FilesModuleDisplayProps {
  module: ProfileModuleDto;
  /** Optional: resolve file path to full URL (e.g. for Capacitor/embed). */
  getFileUrl?: (path: string) => string;
  chrome?: PublicProfileModuleChrome;
}

type FileItem = {
  id?: string;
  file: {
    id: string;
    path: string;
    [key: string]: unknown;
  };
  title?: string;
  description?: string;
  order?: number;
  visible?: boolean;
};

type FilesModuleConfig = {
  type: 'files';
  files: FileItem[];
  showDescriptions?: boolean;
  showFileSize?: boolean;
};

function getFilesConfig(rawConfig: Record<string, unknown>): FilesModuleConfig {
  return {
    type: 'files',
    files: (rawConfig.files as FileItem[]) || [],
    showDescriptions: rawConfig.showDescriptions as boolean | undefined,
    showFileSize: rawConfig.showFileSize as boolean | undefined,
  };
}

function formatFileSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Displays downloadable files in a list with download button.
 */
export function FilesModuleDisplay({
  module,
  getFileUrl = (path) => path,
  chrome,
}: FilesModuleDisplayProps) {
  const layout = useProfileLayout();
  const copy = chrome ?? resolvePublicProfileModuleChrome(undefined);
  const config = getFilesConfig(module.config || {});

  const visibleFiles = (config.files || [])
    .filter((file) => file.visible !== false && file.file?.path)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  if (visibleFiles.length === 0) {
    return null;
  }

  const showDescriptions = config.showDescriptions !== false;
  const showFileSize = config.showFileSize !== false;

  return (
    <div
      className={cn(
        'w-full min-w-0',
        layout === 'classic' ? 'flex flex-col' : 'flex flex-col gap-2.5',
      )}
    >
      {visibleFiles.map((file, index) => {
        const fileSize =
          showFileSize && file.file && 'size' in file.file
            ? formatFileSize(file.file.size as number)
            : null;
        const description = [
          showDescriptions && file.description ? file.description : null,
          fileSize,
        ]
          .filter(Boolean)
          .join(' · ');
        const href = getFileUrl(file.file.path);
        const title = file.title?.trim() || copy.fileFallback;

        // Classic handover: whole row is the download target — no trailing
        // outline/icon button (those sat at the far gutter and looked broken).
        if (layout === 'classic') {
          return (
            <ModuleEntryCard key={file.file?.id ?? index}>
              <a
                href={href}
                download
                aria-label={`${copy.download}: ${title}`}
                className="block min-w-0"
              >
                <ModuleEntryRow>
                  <ModuleEntryIcon>
                    <File />
                  </ModuleEntryIcon>
                  <ModuleEntryTitleBlock
                    title={title}
                    description={description || undefined}
                  />
                </ModuleEntryRow>
              </a>
            </ModuleEntryCard>
          );
        }

        if (layout === 'centered') {
          return (
            <ModuleEntryCard key={file.file?.id ?? index}>
              <a
                href={href}
                download
                aria-label={`${copy.download}: ${title}`}
                className="block min-w-0"
              >
                <ModuleEntryRow>
                  <ModuleEntryIcon>
                    <File />
                  </ModuleEntryIcon>
                  <ModuleEntryTitleBlock
                    title={title}
                    description={description || undefined}
                  />
                  <Download
                    className="h-4 w-4 shrink-0"
                    style={{ stroke: 'var(--faint)' }}
                    strokeWidth={1.8}
                    aria-hidden
                  />
                </ModuleEntryRow>
              </a>
            </ModuleEntryCard>
          );
        }

        return (
          <ModuleEntryCard key={file.file?.id ?? index}>
            <ModuleEntryRow>
              <ModuleEntryIcon>
                <File />
              </ModuleEntryIcon>
              <ModuleEntryTitleBlock
                title={title}
                description={description || undefined}
              />
              <Button
                variant="outline"
                size="icon"
                asChild
                className="shrink-0"
              >
                <a href={href} download aria-label={copy.download}>
                  <Download className="h-4 w-4" />
                </a>
              </Button>
            </ModuleEntryRow>
          </ModuleEntryCard>
        );
      })}
    </div>
  );
}
